import type { TabsProps } from "antd";
import { Skeleton, Tabs } from "antd";
import { FileText, Landmark, Link2, Shield, User } from "lucide-react";
import { useState } from "react";

import Modal from "@/components/Modal";
import { usePermission } from "@/hooks/usePermission";

import { BasicInfoTab } from "./components/BasicInfoTab";
import { EntityHeaderCard } from "./components/EntityHeaderCard";
import { LegalInfoTab } from "./components/LegalInfoTab";
import { LicenseTab } from "./components/LicenseTab";
import { RelatedInfoTab } from "./components/RelatedInfoTab";
import { SyncFooterButton } from "./components/SyncFooterButton";
import { useEntityDetail } from "./hooks/useEntityDetail";
import { useEntityLicense } from "./hooks/useEntityLicense";
import { useEntitySync } from "./hooks/useEntitySync";

/** 工商实体详情 Modal props */
export interface EntityDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** 工商实体 ID */
  entityId: string;
  /** 是否显示底部操作栏 */
  showFooter?: boolean;
  /** 关联门店点击回调 */
  onClickShop?: (id: string) => void;
  /** 关联代理点击回调 */
  onClickAgent?: (id: string) => void;
  /** 关联账号点击回调 */
  onClickAccount?: (id: string) => void;
}

/**
 * 工商实体详情 Modal
 * 结构：信息名片 + Tabs（工商 / 法人 / 证照 / 关联）
 * 证照：有编辑权限时可在 Tab 内直接上传并落库（对齐原型，无需先点编辑）
 */
export const EntityDetailModal = ({
  open,
  onClose,
  entityId,
  showFooter = true,
  onClickShop,
  onClickAgent,
  onClickAccount,
}: EntityDetailModalProps) => {
  /** 当前 Tab key */
  const [detailTab, setDetailTab] = useState("basic");
  const canEditLicense = usePermission("ops:entities:edit");
  const { entityDetail, loading, applyDetail } = useEntityDetail(entityId, open);
  const { saving, persistLicenseFileUrl } = useEntityLicense({
    entityId,
    applyDetail,
  });
  const { syncPhase, getSyncRemaining, handleSync } = useEntitySync({
    entityId,
    open,
    lastSyncAt: entityDetail?.lastSyncAt,
    onSyncSuccess: applyDetail,
  });

  /** Tab 配置 */
  const tabItems: TabsProps["items"] = [
    {
      key: "basic",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          工商信息
        </span>
      ),
      children: <BasicInfoTab detail={entityDetail} />,
    },
    {
      key: "legal",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <User className="w-3.5 h-3.5" />
          法定代表人信息
        </span>
      ),
      children: <LegalInfoTab detail={entityDetail} />,
    },
    {
      key: "license",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          证照管理
        </span>
      ),
      children: (
        <LicenseTab
          detail={entityDetail}
          canEdit={canEditLicense}
          saving={saving}
          onLicensePersist={(url) => void persistLicenseFileUrl(url)}
        />
      ),
    },
    {
      key: "related",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <Link2 className="w-3.5 h-3.5" />
          关联信息
        </span>
      ),
      children: (
        <RelatedInfoTab
          bindInfo={entityDetail?.bindInfo}
          onClickShop={onClickShop}
          onClickAgent={onClickAgent}
          onClickAccount={onClickAccount}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      centered
      title="工商实体详情"
      titleIcon={<Landmark className="w-5 h-5 text-orange-500" />}
      footer={
        showFooter ? (
          <div className="flex justify-end gap-2">
            <SyncFooterButton
              entityId={entityId}
              syncPhase={syncPhase}
              getSyncRemaining={getSyncRemaining}
              onSync={handleSync}
            />
          </div>
        ) : null
      }
      styles={{
        content: { height: "80vh" },
      }}
    >
      <Skeleton loading={loading} active>
        <EntityHeaderCard detail={entityDetail} />
        <Tabs
          className="mt-2 [&_.ant-tabs-content-holder]:px-1 [&_.ant-tabs-nav]:px-1"
          activeKey={detailTab}
          onChange={setDetailTab}
          items={tabItems}
        />
      </Skeleton>
    </Modal>
  );
};
